import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import classNames from 'classnames';

import ArrowRightIcon from '@svg/arrows-right.svg';
import { useAppContext } from '@contexts/AppContext';
import { Link } from '@ui/Link';
import { Skeleton } from '@ui/Skeleton';
import { MenuLevel } from '@components/MenuLevel';
import { NAMESPACE_ICONS_MAP } from '@components/Header/utils';

import s from './HeaderCatalogMenuDesktop.sss';

interface StaticLink {
  title: string,
  icon: React.FC<React.SVGProps<SVGAElement>>,
  href: string,
}

export interface CatalogTree {
  id: number,
  name: string,
  slug: string,
  subCatalogs: CatalogTree[],
}

interface Props {
  catalogTree?: CatalogTree | null,
  staticLinks: StaticLink[],
  isCatalogTreeLoading?: boolean,
}

export const HeaderCatalogMenuDesktop: React.FC<Props> = ({
  catalogTree,
  staticLinks,
  isCatalogTreeLoading,
}) => {
  const history = useHistory();

  const { isOutlet } = useAppContext();

  const [activeLevel, setActiveLevel] = useState<CatalogTree>();

  const createHref = (url: string) => history.createHref({ pathname: url });
  const subCatalogs = catalogTree?.subCatalogs.filter((catalog) => !!catalog.subCatalogs);

  const handleMenuItemClick = (catalog: CatalogTree) => (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    if (isOutlet) {
      e.preventDefault();

      history.push(`/${catalog.slug}`);
    }
  };

  return (
    <div
      className={s.root}
      onMouseLeave={() => setActiveLevel(undefined)}
    >
      <div className={s.container}>
        <ul className={s.list}>
          {isCatalogTreeLoading ? (
            Array(10).fill(null).map((_, i) => (
              <li
                key={i}
                className={s.item}
              >
                <Skeleton
                  width='100%'
                  height='40px'
                />
              </li>
            ))
          ) : (
            catalogTree?.subCatalogs.map((catalog) => {
              const Icon = NAMESPACE_ICONS_MAP[catalog.slug];
  
              return (
                <li
                  key={catalog.slug}
                  className={s.item}
                >
                  <Link
                    className={classNames(
                      s.link,
                      { [s.linkStateActive]: catalog.slug === activeLevel?.slug },
                    )}
                    onClick={handleMenuItemClick(catalog)}
                    href={createHref(`/${catalog.slug}`)}
                    onMouseEnter={() => setActiveLevel(catalog)}
                  >
                    <div className={s.nameBlock}>
                      <Icon
                        className={s.icon}
                        width='24'
                        height='24'
                      />
                      {catalog.name}
                    </div>
  
                    <ArrowRightIcon
                      className={s.arrow}
                      width='24'
                      height='24'
                    />
                  </Link>
                </li>
              );
            })
          )}

          {staticLinks.map((staticLink, i) => {
            const Icon = staticLink.icon;

            return (
              <li
                key={i}
                className={s.item}
              >
                <Link
                  className={s.link}
                  href={staticLink.href}
                  onMouseEnter={() => setActiveLevel(undefined)}
                >
                  <Icon
                    className={s.icon}
                    width='24'
                    height='24'
                  />
                  {staticLink.title}
                </Link>
              </li>
            );
          })}
        </ul>

        {!!activeLevel && (
          subCatalogs?.map((catalog) => (
            <MenuLevel
              key={catalog.slug}
              className={classNames({ [s.hidden]: activeLevel?.slug !== catalog.slug })}
              subCatalogsData={catalog.subCatalogs}
              activeLevelUrl={catalog.slug}
            />
          ))
        )}
      </div>
    </div>
  );
};
